import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Rows3, Rows4 } from 'lucide-react';

/**
 * Two-state density toggle (Comfortable / Compact) for tabular pages.
 *
 * Persists choice to localStorage under `storageKey` (per table). Render next
 * to <ColumnsMenu> in the slim toolbar above the table.
 *
 * Apply the returned `densityClass` to the <Table> via Tailwind's child
 * selectors — overrides TableCell padding without forcing component changes:
 *   <Table className={densityClass}>...
 *
 * The `Compact` preset trims top/bottom padding to ~6 px and side padding to
 * 12 px, so a 1080p screen fits ~2x more rows per scroll than the default.
 */
export const DensityToggle = ({ density, onChange, testid = 'density-toggle' }) => (
  <div
    className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5"
    data-testid={`${testid}-group`}
    role="group"
    aria-label="Row density"
  >
    <Button
      variant={density === 'comfortable' ? 'secondary' : 'ghost'}
      size="sm"
      className="h-7 px-2 gap-1.5 text-xs"
      onClick={() => onChange('comfortable')}
      data-testid={`${testid}-comfortable`}
      title="Comfortable rows"
      aria-pressed={density === 'comfortable'}
    >
      <Rows3 className="w-3.5 h-3.5" />
      Cozy
    </Button>
    <Button
      variant={density === 'compact' ? 'secondary' : 'ghost'}
      size="sm"
      className="h-7 px-2 gap-1.5 text-xs"
      onClick={() => onChange('compact')}
      data-testid={`${testid}-compact`}
      title="Compact rows — fit more on screen"
      aria-pressed={density === 'compact'}
    >
      <Rows4 className="w-3.5 h-3.5" />
      Compact
    </Button>
  </div>
);

/**
 * Hook: returns [density, setDensity, densityClass] for a given page.
 * densityClass is a Tailwind className you spread onto the <Table>.
 */
export const useDensity = (storageKey, defaultDensity = 'comfortable') => {
  const [density, setDensity] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === 'comfortable' || raw === 'compact') return raw;
    } catch (_) {
      // ignore
    }
    return defaultDensity;
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, density);
    } catch (_) {
      // localStorage disabled — choice simply won't survive a reload
    }
  }, [density, storageKey]);
  // Tailwind arbitrary child selectors. `[&_td]:py-1.5` applies py-1.5 to
  // every <td> descendant of the element wearing this class — keeps the
  // override scoped to the table that opts in. We also dial down inline
  // avatar circles (`.rounded-full` blocks with explicit w/h) since those
  // set the row's intrinsic height and would otherwise cap the compaction.
  const densityClass =
    density === 'compact'
      ? [
          '[&_td]:py-0.5 [&_th]:py-1.5',
          '[&_td]:px-2.5 [&_th]:px-2.5',
          'text-[12.5px]',
          // Shrink avatar circles from 32-40px to 24px when present
          '[&_td_.w-10.h-10]:!w-7 [&_td_.w-10.h-10]:!h-7',
          '[&_td_.w-8.h-8]:!w-6 [&_td_.w-8.h-8]:!h-6',
        ].join(' ')
      : '';
  return [density, setDensity, densityClass];
};
